import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import { fmtTime, fmtDayKey, localDayKey } from "@/lib/format";

// Section-aware review body — never renders undefined/null/raw DB values.
// Missing information shows "Not provided" instead of a blank or broken string.
export function SectionBody({ k, s, t }) {
  const np = t("u360_not_provided");
    if (k === "schedule") {
    const entries = (Array.isArray(s?.entries) ? s.entries : []).filter((e) => e && (e.title || e.date));
    if (!entries.length) return <>{np}</>;
    // Relative day names read faster on a phone: "Today · 7 AM", "Sat, Oct 3 · 7 AM–12 PM"
    const fmtDay = (d) => {
      const key = String(d || "").slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return np;
      if (key === localDayKey()) return t("sched_today");
      const tm = new Date(); tm.setDate(tm.getDate() + 1);
      if (key === localDayKey(tm)) return t("sched_quick_tomorrow");
      const [y, m, dd] = key.split("-").map(Number);
      return new Date(y, m - 1, dd).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    };
    const tfmtC = (hm) => fmtTime(`2000-01-01T${hm}:00`, { hour: "numeric", minute: "2-digit" }).replace(/:00(?=\s)/, "");
    const tfmt = (hm) => fmtTime(`2000-01-01T${hm}:00`, { hour: "numeric", minute: "2-digit" });
    // Default work window written on approval when no explicit times were stated.
    const defSpan = `${tfmt("07:00")} – ${tfmt("15:30")} ${t("u360_time_default")}`;
    return (
      <ul className="space-y-1">
        {entries.map((e, i) => (
          <li key={i} data-testid={`u360-sched-entry-${i}`}>
            <span className="font-semibold text-slate-800">{e.title || np}</span>
            {` — ${fmtDay(e.date)} · ${(e.start && e.end) ? `${tfmtC(e.start)}–${tfmtC(e.end)}` : defSpan}`}
            {e.crew ? ` · ${e.crew}` : ""}
          </li>
        ))}
      </ul>
    );
  }
  if (k === "timecards") {
    const entries = (Array.isArray(s?.entries) ? s.entries : []).filter((e) => e && (e.name || e.hours != null));
    return <>{entries.length ? entries.map((e) => `${e.name || np}: ${e.hours ?? np}h`).join(", ") : np}</>;
  }
  if (k === "checklist") {
    return <>{s?.name || np}</>;
  }
  if (k === "tasks") {
    const items = (Array.isArray(s?.items) ? s.items : []).map((i) => i?.title).filter(Boolean);
    return <>{items.length ? items.join("; ") : np}</>;
  }
  if (k === "change_order") {
    const amt = s?.amount != null && s.amount !== "" ? `$${Number(s.amount).toLocaleString()}` : np;
    return <>{`${s?.title || np} — ${amt}${s?.reason ? ` (${s.reason})` : ""}`}</>;
  }
  if (k === "equipment") {
    const items = (Array.isArray(s?.items) ? s.items : []).filter((i) => i && i.equipment_hint);
    if (!items.length) return <>{np}</>;
    return <>{items.map((i) => `${i.equipment_name || i.equipment_hint}: ${i.hours != null ? `${i.hours}h` : np}`).join(", ")}</>;
  }
  if (k === "receipt") {
    const amt = s?.total != null && s.total !== "" ? `$${Number(s.total).toLocaleString()}` : np;
    return <>{`${s?.vendor || np} — ${amt}${s?.receipt_date ? ` · ${s.receipt_date}` : ""}`}</>;
  }
  return <>{s?.text || s?.title || np}</>;
}

// Recognizable field button — opens the unified Assistant in 360 Update mode.
export function Update360Button({ onClick, compact = false, testid = "btn-360-update" }) {
  const { t } = useTranslation();
  if (compact) {
    return (
      <button onClick={onClick} data-testid={testid} aria-label="360 Assistant"
        className="inline-flex min-h-[52px] w-full flex-col items-center justify-center gap-0.5 rounded-xl border border-slate-300 bg-white px-1 text-[10px] font-bold leading-tight text-slate-700 active:scale-95 sm:min-h-[44px] sm:w-auto sm:flex-row sm:gap-2 sm:rounded-full sm:border-0 sm:bg-slateink sm:px-4 sm:text-sm sm:text-white">
        <Sparkles className="h-4 w-4" /> <span>360 Assistant</span>
      </button>
    );
  }
  return (
    <button onClick={onClick} data-testid={testid}
      className="flex w-full flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-slateink bg-slateink p-4 text-white shadow-lg transition-all active:scale-95 min-h-[88px]">
      <span className="flex items-center gap-2 text-sm font-bold"><Sparkles className="h-5 w-5" /> 360 Assistant</span>
      <span className="text-[11px] font-medium text-slate-300">{t("u360_helper")}</span>
    </button>
  );
}
